import { Button } from '@aqarmap/aqarmap-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '~/components/Loader';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import { UserCardFormHandle } from '~/components/UserCardForm/types';
import { RequestStatusEnum, RoutesEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { useFullRoutes, useQuery, useWallet } from '~/hooks';
import { formatNumber } from '~/lib';
import FeatureSummary from '~/pages/FeatureListing/Checkout/FeatureSummary';
import {
  findListingFeature,
  getFeatureAmount,
  markListingFeatured,
} from '~/pages/FeatureListing/utils';
import { useGetFeaturedListingRulesQuery } from '~/redux/api/featureListingApiSlice';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';
import { useGetListingDetailsQuery } from '~/redux/api/userApiSlice';
import { setFooterDisplay } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import { SelectedPaymentType } from '~/types/paymentTypes';
import useBreakpoint from '~/hooks/useBreakpoint';
import '~/cardsIcons.css';

export default function FeatureCheckout() {
  const userCardFormRef = useRef<UserCardFormHandle>(null);
  const { id, locale, featureKey } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { status } = useQuery();
  const { t, i18n } = useTranslation('app');
  const { ADD_LISTING_URL } = useFullRoutes();
  const { mobileScreen } = useBreakpoint();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);

  const { walletBalance } = useWallet({});
  const {
    data: rulesData,
    isLoading: isRulesLoading,
    isSuccess: isRulesSuccess,
    isError: isRulesError,
  } = useGetFeaturedListingRulesQuery(
    { id: id as string, locale: i18n.language },
    { skip: !id },
  );
  const {
    data: listingDetailsData,
    isFetching: isFetchingListingDetails,
    isError: isListingDetailsError,
  } = useGetListingDetailsQuery(id);
  const [postPaymentPay, { isLoading: isPayLoading }] =
    usePostPaymentPayMutation();

  const feature = findListingFeature(rulesData, featureKey) ?? null;
  const amount = getFeatureAmount(feature);
  const featurePageUrl = ADD_LISTING_URL.FEATURE(`${id}`);
  const slug = listingDetailsData?.listing?.section?.slug;

  const listingDetailUrl = `${process.env.REACT_APP_BASE_URL}/${locale}/${RoutesEnum.LISTING}/${id}${slug ? `-${slug}` : ''}`;
  const isSuccessReturn = status === RequestStatusEnum.SUCCESS;

  const payWithWallet = !!selectedPayment?.payWithWallet;
  const hasSelectedPayment =
    payWithWallet || !!selectedPayment?.isNew || Boolean(selectedPayment?.id);
  const isWalletBalanceInsufficient = payWithWallet && walletBalance < amount;
  const isSubmitDisabled =
    !feature ||
    !hasSelectedPayment ||
    isPayLoading ||
    isPayfortLoading ||
    isWalletBalanceInsufficient;

  const featurePaymentParams = {
    operationType: PaymentOperationTypeEnum.LISTING_FEATURE,
    listingId: id,
    listingFeaturedType: feature?.listingFeaturedType,
    amount,
  };

  const handlePaymentResponse = (response: {
    data?: { status?: string; message?: string; '3ds_url'?: string };
    error?: unknown;
  }) => {
    const { data, error } = response;
    if (data?.status === RequestStatusEnum.SUCCESS) {
      markListingFeatured({ id, costType: 'money', feature });
      window.location.href = listingDetailUrl;
      return;
    }
    if (data?.status === '3ds_required' && data?.['3ds_url']) {
      window.location.href = data['3ds_url'];
      return;
    }
    const errorMessage =
      data?.message ||
      (error as { data?: { message?: string } } | undefined)?.data?.message;
    setCheckoutError(t(errorMessage || 'somethingWrong'));
  };

  const handlePayWithWallet = async () => {
    if (isWalletBalanceInsufficient) return;
    handlePaymentResponse(
      await postPaymentPay({
        ...featurePaymentParams,
        paymentMethod: PaymentMethodEnum.WALLET,
      }),
    );
  };

  const handlePayWithSavedCard = async () => {
    if (!selectedPayment) return;
    handlePaymentResponse(
      await postPaymentPay({
        ...featurePaymentParams,
        paymentMethod: PaymentMethodEnum.SAVED_CARD,
        cardId: selectedPayment.id,
      }),
    );
  };

  const onCheckoutSubmit = () => {
    if (!feature) return;

    if (payWithWallet) {
      handlePayWithWallet();
    } else if (selectedPayment?.isNew && userCardFormRef.current) {
      setIsPayfortLoading(true);
      userCardFormRef.current.submitUserCard({
        ...featurePaymentParams,
        returnTo: `${process.env.REACT_APP_BASE_URL}${ADD_LISTING_URL.FEATURE_CHECKOUT(`${id}`, `${featureKey}`)}`,
      });
    } else {
      handlePayWithSavedCard();
    }
  };

  useEffect(() => {
    if (isRulesSuccess && !feature && !isSuccessReturn)
      navigate(featurePageUrl, { replace: true });
  }, [isRulesSuccess, feature, isSuccessReturn]);

  useEffect(() => {
    if (status === RequestStatusEnum.FAILED) {
      setCheckoutError(t('somethingWrong'));
      setSelectedPayment({ isNew: true, id: 0 });
    }
  }, [status]);

  useEffect(() => {
    if (!isSuccessReturn) return;

    if (!isRulesSuccess && !isRulesError) return;
    if (
      !isFetchingListingDetails &&
      (listingDetailsData || isListingDetailsError)
    ) {
      markListingFeatured({ id, costType: 'money', feature });
      window.location.href = listingDetailUrl;
    }
  }, [
    isSuccessReturn,
    isRulesSuccess,
    isRulesError,
    isFetchingListingDetails,
    listingDetailsData,
    isListingDetailsError,
  ]);

  useEffect(() => {
    if (mobileScreen) dispatch(setFooterDisplay('hide'));
    return () => {
      dispatch(setFooterDisplay('show'));
    };
  }, [mobileScreen]);

  useEffect(() => {
    document.title = `${t('featureListingPageTitle')}|${t('aqarmap')}`;
  }, [t, i18n.language]);

  if (isSuccessReturn) return <Loader />;

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex w-full flex-col-reverse justify-between gap-4x md:flex-row px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('featurePaymentDetails')}
          enableWallet
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => {
              setIsPayfortLoading(false);
              setCheckoutError(t('somethingWrong'));
            },
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          isLoading={isRulesLoading || !isRulesSuccess}
          title={t('featurePaymentSummary')}
          totalFeesLabel={t('totalFees')}
          fees={`${formatNumber(amount)} ${t('egp')}`}
          submitBtnLabel={t('pay')}
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isPayLoading || isPayfortLoading,
            disabled: isSubmitDisabled,
          }}
          summary={
            <FeatureSummary feature={feature} changeUrl={featurePageUrl} />
          }
        />
        <div className="fixed bottom-0 left-0 z-[40] flex w-full items-center justify-between border-t border-gray bg-white px-2x pt-2x pb-3x md:hidden">
          <Button
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isPayLoading || isPayfortLoading}
            disabled={isSubmitDisabled}
          >
            {t('pay')}
          </Button>
        </div>
      </div>
    </div>
  );
}
